.wrapper {
    display: flex;
    flex-direction: column;
    gap: var(--tempest-space-3);
    max-width: 100%;
    font-family: var(--tempest-font-sans);
}

.frame {
    position: relative;
    width: 100%;
    overflow: hidden;
    border: 1px solid var(--tempest-border);
    border-radius: var(--tempest-radius-lg);
    background-color: var(--tempest-surface);
    /*
     * `grab` rather than `move`: nothing is being moved to a destination, the
     * image is being dragged under a fixed frame.
     */
    cursor: grab;
    /*
     * The frame owns dragging on touch, so the browser must not also scroll the
     * page from the same gesture.
     */
    touch-action: none;
    user-select: none;
}

.frame:active {
    cursor: grabbing;
}

.frame:focus-visible {
    outline: 2px solid var(--tempest-primary);
    outline-offset: 2px;
}

.circle {
    border-radius: var(--tempest-radius-full);
}

.image {
    position: absolute;
    top: 50%;
    left: 50%;
    /*
     * Both `max-*`, not just the width. The size is set inline from the zoom, so
     * either cap silently overrides it — and an app reset carrying
     * `img { max-height: 100% }` is enough, since a 0-0-1 element rule meets no
     * competition here. The failure is worse than it looks: `crop()` builds its
     * rectangle from `computeCropRect({ image: natural, ... })` and never reads the
     * rendered element, so the export stays correct while the preview is squashed
     * on one axis. The user drags against a distorted image and gets a framing they
     * never saw. A component that sizes itself inline owns neutralising both caps.
     */
    max-width: none;
    max-height: none;
    pointer-events: none;
    /*
     * Centring lives in `translate` and the pan in the inline `transform`, which
     * are separate CSS properties applied in that order. Keeping them apart means
     * a pan is just `transform: translate(x, y)` — it never has to re-derive the
     * `-50% -50%` centring, and the two cannot overwrite each other.
     */
    translate: -50% -50%;
}

.controls {
    display: flex;
    /*
     * Wraps rather than squeezing. The zoom slider is the flexible item, so on a
     * narrow row it is the one that gives — and a range input has no useful floor
     * of its own, so it gave all the way to `width: 0`, leaving the thumb alone and
     * no visible focus ring while the button beside it kept both. Measured at 390px
     * with a crowded row. Wrapping moves the slider to its own line instead.
     */
    flex-wrap: wrap;
    align-items: center;
    gap: var(--tempest-space-3);
}

.zoom {
    /*
     * `min-width: 0` is what let it collapse to nothing: it exists to let a flex
     * item shrink past its min-content size, which for a range input is the whole
     * control. A real floor keeps the slider draggable and its focus ring visible;
     * the wrap on `.controls` is what absorbs the width this refuses to give up.
     */
    flex: 1 1 6rem;
    min-width: 6rem;
    accent-color: var(--tempest-primary);
}

.zoom:disabled {
    opacity: 0.5;
}

.reset {
    padding: var(--tempest-space-1) var(--tempest-space-3);
    border: 1px solid var(--tempest-border);
    border-radius: var(--tempest-radius-md);
    background-color: var(--tempest-bg);
    color: var(--tempest-text);
    font: inherit;
    font-size: var(--tempest-text-sm);
    cursor: pointer;
}

@media (hover: hover) and (pointer: fine) {
    .reset:hover:not(:disabled) {
        background-color: var(--tempest-surface);
    }
}

.reset:focus-visible {
    outline: 2px solid var(--tempest-primary);
    outline-offset: 2px;
}

.reset:disabled {
    opacity: 0.5;
    cursor: default;
}

.hint {
    margin: 0;
    color: var(--tempest-text-muted);
    font-size: var(--tempest-text-xs);
}

.hint kbd {
    padding: 0 var(--tempest-space-1);
    border: 1px solid var(--tempest-border);
    border-radius: var(--tempest-radius-sm);
    background-color: var(--tempest-surface);
    font-family: var(--tempest-font-mono, monospace);
    font-size: inherit;
}
